import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useTranslation } from "react-i18next"
import { peopleMaskPartLabelKeys, getMaskDisplayName } from "@/i18n/dual-role-strings"
import {
	AddMaskGroupWithMasks,
	addMasks,
	checkPhotoId,
	detectMaskSequentially,
	mappedMasksAtom,
	maskDetectionStateAtom,
	pendingMaskGroupAtom,
	refreshMaskGroupThumbnail,
	removeMask,
	removeMaskGroup,
	selectedMaskAtom,
	selectedMaskGroupAtom,
	showNoDetected,
	showingFloatingMaskingPanelAtom,
	showingMaskingMenuAtom,
	updateMask,
	updateMaskGroup,
} from "@/store/editor/masks-state"
import {
	createBackgroundMask,
	createBrushMask,
	createGradientMask,
	createPeopleMasksForSelection,
	createRadialMask,
	createSkyMask,
	createSubjectMask,
	getMaskIcon,
	MappedMask,
	MappedMaskGroup,
} from "@/store/editor/mask-utils"
import clsx from "clsx"
import { Icon } from "@/components/icons/Icon"
import { Button } from "@/components/ui/Button"
import { ScrollBar } from "@/components/ui/ScrollBar"
import { PropsWithChildren, useCallback, useEffect, useId, useMemo, useState } from "react"
import { showingMaskingPanelAtom } from "@/store/ui-views-state"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { BaseMask, Mask, PeopleMaskPart } from "@/core/types"
import { focusAtom } from "jotai-optics"
import { Spinner } from "@/components/ui/Spinner"
import { editingDisabledAtom } from "@/store/app-state"
import { Checkbox } from "@/components/ui/Checkbox"
import {
	maskOverlayPreviewStateAtom,
	maskOverlaySettingsAtom,
	setMaskOverlay,
} from "@/store/editor/mask-overlays"
import { analytics } from "@/telemetry/amplitude"
import { useEditor } from "@/utils/useEditor"
import { useShortcuts } from "@/utils/useShortcuts"
import { MainAIMaskResolver } from "@/io/assets/ai-mask/main-ai-mask-resolver"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import { InputDialog } from "../Dialogs/InputDialog"
import { pushDialog, resetDialogs } from "@/store/dialogs-state"
import { currentPhotoIdAtom } from "@/store/photo/photos-state"
import { actionShortcuts } from "@/shortcuts/shortcuts"
import type {
	PeopleMaskPartAvailability,
	VisiblePeopleSegmentationResult,
} from "@/io/assets/ai-mask/ai-mask-people-provider"
import { useResizeObserver } from "@/utils/useResizeObserver"
import { isRetouchingEnabled } from "@/utils/measurement/feature-flag"
import {
	clearFailedMaskGroupThumbnailReservations,
	getMaskGroupThumbnail,
	getMaskGroupDigest,
	hasMaskGroupThumbnail,
	maskGroupThumbnailCacheAtom,
	reserveMaskGroupThumbnails,
	resetMaskGroupThumbnails,
	setMaskGroupThumbnailBlob,
} from "@/store/editor/mask-thumbnails"
import { useEditorPhysicalTransformKey } from "@/components/Masking/useEditorPhysicalTransformKey"
import { PeopleDetectionLoadingPanel } from "@/components/People/PeopleDetectionLoadingPanel"
import { MainMaskCache } from "@/storage/mask-cache"
import { projectAtom } from "@/store/project/projects-state"
import { store } from "@/store/store"

const maskPanelBottomReserve = 56
const maskPanelVerticalPadding = 16
const maskPanelVerticalGap = 8
const maskPanelAnimationDuration = 200

export const FloatingMasksPanel = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const [overlaySettings, setOverlaySettings] = useAtom(maskOverlaySettingsAtom)
	const [editingDisabled] = useAtom(editingDisabledAtom)
	const [showingFloatingPanel] = useAtom(showingFloatingMaskingPanelAtom)
	const [isMaskingActive, setIsMaskingActive] = useAtom(showingMaskingPanelAtom)
	const mappedMasks = useAtomValue(mappedMasksAtom)
	const currentPhotoId = useAtomValue(currentPhotoIdAtom)
	const projectId = useAtomValue(projectAtom)?.id
	const thumbnailCache = useAtomValue(maskGroupThumbnailCacheAtom)
	const [selectedGroup, setSelectedGroup] = useAtom(selectedMaskGroupAtom)
	const peopleMaskingEnabled = isRetouchingEnabled()
	const { observeRef: observePanelParent, dimensions: panelParentDimensions } =
		useResizeObserver()
	const { observeRef: observeHeader, dimensions: headerDimensions } = useResizeObserver()
	const { observeRef: observeFooter, dimensions: footerDimensions } = useResizeObserver()
	const { observeRef: observeMaskListContent, dimensions: maskListContentDimensions } =
		useResizeObserver()

	const [pendingGroup] = useAtom(pendingMaskGroupAtom)

	useShortcuts({
		keys: [actionShortcuts.del, actionShortcuts.backspace, actionShortcuts.delete],
		action: () => {
			if (selectedGroup) {
				removeMaskGroup(selectedGroup)
			}
		},
		trackingLabel: "delete_mask",
		disabled: !isMaskingActive || !selectedGroup,
	})

	useShortcuts({
		keys: [actionShortcuts.enter, actionShortcuts.escape],
		action: () => {
			if (selectedGroup) {
				setSelectedGroup(null)
			} else if (isMaskingActive) {
				setIsMaskingActive(false)
			}
		},
		trackingLabel: selectedGroup ? "unselect_mask_group" : "cancel_masking",
		disabled: !isMaskingActive,
	})

	const groups = useMemo(
		() => [...pendingGroup, ...mappedMasks.slice().reverse()],
		[mappedMasks, pendingGroup],
	)
	const transformKey = useEditorPhysicalTransformKey(currentPhotoId)
	const thumbnailGroups = useMemo(
		() =>
			new Map(
				mappedMasks.map((group) => [
					group.properties.id,
					{
						group,
						maskGroupDigest: getMaskGroupDigest(group.properties.masks),
					},
				]),
			),
		[mappedMasks],
	)

	useEffect(() => {
		if (!isMaskingActive) {
			clearFailedMaskGroupThumbnailReservations()
		}
	}, [isMaskingActive])

	useEffect(() => {
		if (thumbnailCache.photoId && thumbnailCache.photoId !== currentPhotoId) {
			resetMaskGroupThumbnails()
		}
	}, [currentPhotoId, thumbnailCache.photoId])

	useEffect(() => {
		if (
			!peopleMaskingEnabled ||
			!isMaskingActive ||
			!currentPhotoId ||
			!transformKey ||
			thumbnailGroups.size === 0
		) {
			return
		}

		const staleGroups = Array.from(thumbnailGroups.values()).filter(
			({ group, maskGroupDigest }) =>
				!hasMaskGroupThumbnail(
					thumbnailCache,
					currentPhotoId,
					group.properties.id,
					transformKey,
					maskGroupDigest,
				),
		)
		if (staleGroups.length === 0) {
			return
		}

		const controller = new AbortController()
		void (async () => {
			const persistedThumbnails = await MainMaskCache.readMaskGroupThumbnails(currentPhotoId)
			if (
				controller.signal.aborted ||
				editor.photo.id !== currentPhotoId ||
				store.get(projectAtom)?.id !== projectId
			) {
				return
			}

			const persistedByGroup = new Map(
				persistedThumbnails.map((record) => [record.groupId, record]),
			)
			const hydratedThumbnailIds: string[] = []
			const missingGroups = staleGroups.filter(({ group, maskGroupDigest }) => {
				const record = persistedByGroup.get(group.properties.id)
				const hydrated =
					record?.transformKey === transformKey &&
					record.maskGroupDigest === maskGroupDigest &&
					setMaskGroupThumbnailBlob(
						currentPhotoId,
						group.properties.id,
						transformKey,
						maskGroupDigest,
						record.data,
					)
				if (hydrated) {
					hydratedThumbnailIds.push(record.id)
				}
				return !hydrated
			})
			await MainMaskCache.touchMaskGroupThumbnails(hydratedThumbnailIds)
			if (
				controller.signal.aborted ||
				editor.photo.id !== currentPhotoId ||
				store.get(projectAtom)?.id !== projectId
			) {
				return
			}
			const reservedGroups = reserveMaskGroupThumbnails(
				currentPhotoId,
				missingGroups.map(({ group, maskGroupDigest }) => ({
					groupId: group.properties.id,
					maskGroupDigest,
				})),
				transformKey,
			)

			await Promise.all(
				reservedGroups.map(({ groupId, maskGroupDigest }) =>
					refreshMaskGroupThumbnail(groupId, transformKey, maskGroupDigest),
				),
			)
		})()

		return () => controller.abort()
	}, [
		currentPhotoId,
		editor,
		isMaskingActive,
		peopleMaskingEnabled,
		projectId,
		thumbnailCache,
		thumbnailGroups,
		transformKey,
	])

	const handlePanelRef = useCallback(
		(node: HTMLDivElement | null) => {
			observePanelParent(node?.parentElement ?? null)
		},
		[observePanelParent],
	)

	const maskListHeight = useMemo(() => {
		const contentHeight = maskListContentDimensions.scrollHeight
		const parentHeight = panelParentDimensions.clientHeight

		if (!contentHeight || !parentHeight) {
			return undefined
		}

		const availableHeight =
			parentHeight -
			maskPanelBottomReserve -
			maskPanelVerticalPadding -
			maskPanelVerticalGap -
			(headerDimensions.clientHeight ?? 0) -
			(footerDimensions.clientHeight ?? 0)

		return Math.max(0, Math.min(contentHeight, availableHeight))
	}, [
		footerDimensions.clientHeight,
		headerDimensions.clientHeight,
		maskListContentDimensions.scrollHeight,
		panelParentDimensions.clientHeight,
	])

	if (!isMaskingActive) {
		return null
	}

	const toggleShowOverlay = (showOverlay: boolean) => {
		setOverlaySettings({ ...overlaySettings, showOverlay })
	}

	return (
		<div
			ref={handlePanelRef}
			aria-label={t("floatingMasksPanel.ariaFloatingMasksPanel")}
			className={clsx(
				showingFloatingPanel ? "opacity-100" : "opacity-0",
				"absolute right-0 top-0 z-10 flex max-h-[calc(100%-3.5rem)] flex-col overflow-hidden border border-base-900 bg-base-800 p-2 transition-opacity",
			)}
		>
			<div
				aria-disabled={editingDisabled}
				className="flex min-h-0 cursor-default flex-col gap-1 transition-all aria-disabled:pointer-events-none aria-disabled:opacity-50"
				style={{
					transitionDuration: `${200}ms`,
				}}
				onPointerDown={(e) => {
					// Prevent viewport interactions
					e.stopPropagation()
					e.preventDefault()
				}}
			>
				<div
					aria-disabled={editingDisabled}
					className="flex min-h-0 cursor-default flex-col gap-1 aria-disabled:pointer-events-none aria-disabled:opacity-50"
					onPointerDown={(e) => {
						// Prevent viewport interactions
						e.stopPropagation()
						e.preventDefault()
					}}
				>
					<div ref={observeHeader} className="flex shrink-0 flex-col gap-1">
						<div className="pb-1 text-sm font-bold">
							{t("floatingMasksPanel.masks")}
						</div>

						<MaskMenu mode="add" onSelect={AddMaskGroupWithMasks} isMainMenu={true}>
							<Button
								trackingLabel="create_mask"
								aria-label={t("floatingMasksPanel.ariaCreateNewMask")}
								variant="ghost"
								className="justify-start pl-2"
								onFocus={(e) => e.target.blur()}
							>
								<Icon
									name="add"
									className="mr-2 rounded-full bg-accent-500 p-1"
									size={24}
								/>{" "}
								{t("floatingMasksPanel.createNewMask")}
							</Button>
						</MaskMenu>
					</div>

					<div
						className="scrollarea min-h-0 w-56 overflow-y-auto overflow-x-hidden overscroll-contain pr-4 transition-all"
						style={{
							height: maskListHeight,
							transitionDuration: `${maskPanelAnimationDuration}ms`,
						}}
						onPointerDown={(e) => {
							e.stopPropagation()
						}}
						onWheel={(e) => {
							e.stopPropagation()
						}}
					>
						<div ref={observeMaskListContent} className="w-full">
							{groups.map((group) => {
								const maskGroupDigest = thumbnailGroups.get(
									group.properties.id,
								)?.maskGroupDigest
								const thumbnailUrl =
									peopleMaskingEnabled &&
									currentPhotoId &&
									maskGroupDigest &&
									transformKey
										? getMaskGroupThumbnail(
												thumbnailCache,
												currentPhotoId,
												group.properties.id,
												transformKey,
												maskGroupDigest,
											)
										: undefined
								return (
									<MaskGroup
										key={group.properties.id}
										group={group}
										pendingGroup={pendingGroup}
										thumbnailUrl={thumbnailUrl}
									/>
								)
							})}
						</div>
						<ScrollBar visible="hover" />
					</div>

					<div ref={observeFooter} className="flex shrink-0 justify-between pt-1">
						<label className="flex items-center gap-1 text-xs">
							<Checkbox
								trackingLabel="show_overlay"
								aria-label={t("floatingMasksPanel.ariaToggleMaskOverlay")}
								checked={overlaySettings.showOverlay}
								onCheckedChange={toggleShowOverlay}
							/>{" "}
							{t("floatingMasksPanel.showOverlay")}
						</label>
					</div>
				</div>
			</div>
		</div>
	)
}

const MaskGroup = (props: {
	group: MappedMaskGroup
	pendingGroup: MappedMaskGroup[]
	thumbnailUrl?: string
}) => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const [selectedGroup, setSelectedGroup] = useAtom(selectedMaskGroupAtom)
	const setSelectedMask = useSetAtom(selectedMaskAtom)
	const [pendingMasks, setPendingMasks] = useState<MappedMask[]>([])

	// Show masks in reverse order so that most recent is at the top
	const masks = [...pendingMasks, ...props.group.children.slice().reverse()]
	const isPendingGroup = props.pendingGroup.at(0) === props.group
	const isSelected = isPendingGroup || props.group.properties.id === selectedGroup?.properties.id
	const processingPhotoId = useAtomValue(currentPhotoIdAtom)

	const isPendingMask = (mask: MappedMask) => pendingMasks.includes(mask)

	const handleToggleActive = (active: boolean) => {
		updateMaskGroup(props.group, { active })
		saveCurrentAdjustmentState({
			message: `${active ? "Show" : "Hide"} ${props.group.properties.name}`,
		})
	}

	const handleSelectGroup = () => {
		if (isSelected) {
			setSelectedGroup(null)
		} else {
			setSelectedGroup(props.group.properties)
			setSelectedMask(null)
		}
	}

	const handleDeleteGroup = () => {
		removeMaskGroup(props.group)
	}

	const handleRenameGroup = () => {
		pushDialog(InputDialog, {
			title: t("floatingMasksPanel.renameMaskGroup"),
			buttonTitle: t("common:action.rename"),
			initialInput: props.group.properties.name,
			confirmWithEnter: true,
			onConfirm: (name) => {
				resetDialogs()
				updateMaskGroup(props.group, { name })
				saveCurrentAdjustmentState({
					message: `Rename Mask Group`,
					changedValue: name,
				})
			},
			trackingViewName: "rename_mask_group_dialog",
		})
	}

	const handleAddMasks = async (masks: Mask[]) => {
		const detectedMasks: Mask[] = []

		for (const mask of masks) {
			const detectedResult = MainAIMaskResolver.hasMaskDetected(editor.photo, mask)
			if (detectedResult === false) {
				showNoDetected(mask.type)
				continue
			}

			if (detectedResult === true) {
				detectedMasks.push(mask)
				continue
			}

			const mappedMask = {
				properties: mask,
				lens: props.group.lens.prop("masks").at(0),
				group: props.group,
			}

			setPendingMasks((prevState) => [...prevState, mappedMask])

			const detectionResult = await detectMaskSequentially(editor.photo, mask)

			setPendingMasks((prevState) =>
				prevState.filter((value) => value.properties.id !== mask.id),
			)
			if (detectionResult) {
				detectedMasks.push(mask)
			} else {
				showNoDetected(mask.type)
			}
		}

		if (detectedMasks.length > 0 && checkPhotoId(processingPhotoId)) {
			await addMasks(props.group.properties.id, detectedMasks)
		}
	}

	const showOverlay = () => {
		setMaskOverlay(props.group.properties.id)
	}

	const hideOverlay = () => {
		setMaskOverlay(null)
	}

	return (
		<div
			aria-label={t("floatingMasksPanel.ariaMaskGroup")}
			className={clsx(
				"rounded-md p-1",
				isSelected ? "bg-base-900 text-base-100" : "text-base-300",
			)}
		>
			<div
				role="button"
				aria-expanded={isSelected}
				aria-label={t("floatingMasksPanel.maskGroup", {
					var0: props.group.properties.name,
				})}
				tabIndex={0}
				className={clsx(
					"group flex items-center gap-2 px-1 py-0.5 text-sm hover:text-base-100",
					isSelected && "text-base-100",
				)}
				onClick={handleSelectGroup}
				onPointerEnter={showOverlay}
				onPointerLeave={hideOverlay}
			>
				<Icon
					name={isSelected ? "expand_down" : "expand_right"}
					className="shrink-0"
					size={16}
				/>
				<div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded border-2 border-base-300 bg-base-900">
					{props.thumbnailUrl ? (
						<img
							src={props.thumbnailUrl}
							alt=""
							draggable={false}
							className="size-full object-contain"
						/>
					) : (
						<Icon
							name={getMaskIcon(masks[0]?.properties.type ?? "subject")}
							size={20}
						/>
					)}
				</div>
				<div className="flex-1 truncate px-1">
					{(() => {
						const display = getMaskDisplayName(props.group.properties.name)
						return display
							? t(display.key, display.params)
							: props.group.properties.name
					})()}
				</div>
				<div className="hidden shrink-0 items-center gap-1 group-hover:flex">
					<RenameMask onRename={handleRenameGroup} />
					<DeleteMask onDelete={handleDeleteGroup} />
					<ToggleMask
						isActive={props.group.properties.active}
						onUpdate={handleToggleActive}
					/>
				</div>
			</div>
			{isSelected && (
				<>
					<div
						role="list"
						className="pt-1"
						onPointerDown={(e) => {
							e.stopPropagation()
						}}
					>
						{masks.map((mask) => {
							return (
								<MaskItem
									key={mask.properties.id}
									mask={mask}
									isGroupActive={props.group.properties.active}
									isPendingMask={isPendingGroup || isPendingMask(mask)}
								/>
							)
						})}
					</div>
					<div className="ml-2 flex gap-1 pb-0.5 pt-1">
						<MaskMenu
							mode="add"
							onSelect={handleAddMasks}
							isMainMenu={false}
							parentMaskGroupId={props.group.properties.id}
							parentMasks={props.group.properties.masks}
						>
							<Button
								trackingLabel="add"
								variant="outline"
								size="sm"
								className="flex-1 text-base-300"
								onFocus={(e) => e.target.blur()}
							>
								<Icon name="add_mask" size={16} className="mr-1" />
								{t("floatingMasksPanel.add")}
							</Button>
						</MaskMenu>
						<MaskMenu
							mode="subtract"
							onSelect={handleAddMasks}
							isMainMenu={false}
							parentMaskGroupId={props.group.properties.id}
							parentMasks={props.group.properties.masks}
						>
							<Button
								trackingLabel="subtract"
								variant="outline"
								size="sm"
								className="flex-1 text-base-300"
								onFocus={(e) => e.target.blur()}
							>
								<Icon name="subtract_mask" size={16} className="mr-1" />
								{t("floatingMasksPanel.subtract")}
							</Button>
						</MaskMenu>
					</div>
				</>
			)}
		</div>
	)
}

const MaskItem = (props: { mask: MappedMask; isGroupActive: boolean; isPendingMask: boolean }) => {
	const { t } = useTranslation("components")
	const [selectedMask, setSelectedMask] = useAtom(selectedMaskAtom)

	const mask = props.mask.properties
	const isSelected = selectedMask?.properties.id === mask.id

	const detectionStateAtom = useMemo(
		() => focusAtom(maskDetectionStateAtom, (optic) => optic.prop(mask.id)),
		[mask.id],
	)

	const detectionState = useAtomValue(detectionStateAtom)

	const handleToggleActive = async (active: boolean) => {
		updateMask(props.mask, { active })
		await saveCurrentAdjustmentState({
			message: `${active ? "Show" : "Hide"} ${props.mask.properties.name}`,
		})
	}

	const handleSelectMask = () => {
		setSelectedMask(mask)
	}

	const handleDelete = () => {
		void removeMask(props.mask)
	}

	const handleRename = () => {
		pushDialog(InputDialog, {
			title: t("floatingMasksPanel.renameMask"),
			buttonTitle: t("common:action.rename"),
			initialInput: mask.name,
			confirmWithEnter: true,
			onConfirm: (name) => {
				resetDialogs()
				updateMask(props.mask, { name })
				saveCurrentAdjustmentState({
					message: `Rename Mask`,
					changedValue: name,
				})
			},
			trackingViewName: "rename_mask_dialog",
		})
	}

	const showOverlay = () => {
		setMaskOverlay(props.mask.properties.id)
	}

	const hideOverlay = () => {
		setMaskOverlay(null)
	}

	return (
		<div
			role="listitem"
			aria-label={t("floatingMasksPanel.mask", {
				name: mask.name,
				type: mask.type,
				var2: props.isPendingMask ? ", detecting" : "",
			})}
			tabIndex={0}
			className={clsx(
				"group ml-[30px] flex items-center rounded-md p-1 text-sm hover:bg-base-800 hover:text-base-100",
				isSelected ? "text-base-100" : "text-base-300",
			)}
			onClick={handleSelectMask}
			onPointerEnter={showOverlay}
			onPointerLeave={hideOverlay}
		>
			<div>
				<div
					className={clsx(
						"relative flex size-6 items-center justify-center rounded bg-base-900 text-base-400",
						isSelected && "text-base-200 ring-1 ring-base-100",
					)}
				>
					{props.isPendingMask || detectionState === "started" ? (
						<Spinner className="absolute text-accent-500" size={16} />
					) : (
						<Icon size={16} name={getMaskIcon(mask.type)} />
					)}

					{mask.blendMode === "subtract" && (
						<Icon
							name="remove"
							size={14}
							className="absolute rounded-full border-2 border-base-900 bg-base-300 text-base-900"
							style={{
								right: -6,
								top: -6,
							}}
						/>
					)}
				</div>
			</div>

			<div className="flex-1 truncate pl-2 pr-1">
				{(() => {
					const display = getMaskDisplayName(mask.name, mask.type)
					if (!display) return mask.name
					const params = display.params
						? {
								...display.params,
								...(display.params.partKey
									? { part: t(display.params.partKey as string) }
									: {}),
								...(display.params.typeKey
									? { type: t(display.params.typeKey as string) }
									: {}),
							}
						: undefined
					return t(display.key, params)
				})()}
			</div>
			<div className="hidden shrink-0 items-center gap-1 group-hover:flex">
				<RenameMask onRename={handleRename} />
				<DeleteMask onDelete={handleDelete} />
				<ToggleMask
					isActive={props.mask.properties.active}
					onUpdate={handleToggleActive}
					disabled={!props.isGroupActive}
					hidden={!props.isGroupActive && props.mask.properties.active}
				/>
			</div>
		</div>
	)
}

const DeleteMask = (props: { onDelete: () => void }) => {
	const { t } = useTranslation("components")
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		props.onDelete()

		analytics.trackEvent({ label: "delete_mask", type: "click" }, e.target)
	}

	return (
		<Icon
			name="delete"
			role="button"
			aria-label={t("floatingMasksPanel.ariaDeleteMask")}
			className="invisible mr-2 text-base-400 hover:text-base-200 group-hover:visible"
			size={14}
			onClick={handleClick}
		/>
	)
}

const RenameMask = (props: { onRename: () => void }) => {
	const { t } = useTranslation("components")
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()
		props.onRename()

		analytics.trackEvent({ label: "rename_mask", type: "click" }, e.target)
	}

	return (
		<Icon
			name="rename"
			role="button"
			aria-label={t("floatingMasksPanel.ariaRenameMask")}
			className="invisible mr-2 text-base-400 hover:text-base-200 group-hover:visible"
			size={14}
			onClick={handleClick}
		/>
	)
}

const ToggleMask = (props: {
	isActive: boolean
	onUpdate: (active: boolean) => void
	disabled?: boolean
	hidden?: boolean
}) => {
	const { t } = useTranslation("components")
	const handleToggleActive = (e: React.MouseEvent, active: boolean) => {
		e.stopPropagation()
		e.preventDefault()

		if (props.disabled) {
			return
		}

		props.onUpdate(active)

		analytics.trackEvent(
			{ label: "toggle_mask_active", type: "click", value: String(active) },
			e.target,
		)
	}

	return (
		<Icon
			name={props.isActive ? "visible" : "hidden"}
			role="button"
			aria-label={
				props.isActive
					? t("floatingMasksPanel.ariaHideMask")
					: t("floatingMasksPanel.ariaShowMask")
			}
			className={clsx(
				props.hidden
					? "invisible"
					: props.isActive &&
							"visible" &&
							"invisible text-base-400 hover:text-base-200 group-hover:visible",
			)}
			size={14}
			onClick={(e) => handleToggleActive(e, !props.isActive)}
		/>
	)
}

interface MaskMenuProps extends PropsWithChildren {
	mode: "add" | "subtract"
	onSelect: (masks: Mask[], options?: { separateGroups?: boolean }) => void
	isMainMenu: boolean
	parentMaskGroupId?: string
	parentMasks?: Mask[]
}

const selectablePeopleMaskParts: Exclude<PeopleMaskPart, "entire">[] = [
	"facialSkin",
	"bodySkin",
	"eyebrows",
	"eyes",
	"lips",
	"hair",
	"clothing",
]

const disabledPeopleMaskParts = [
	"floatingMasksPanel.teeth",
	"floatingMasksPanel.facialHair",
] as const

const MaskMenu = (props: MaskMenuProps) => {
	const { t } = useTranslation("components")
	const [isExpanded, setIsExpanded] = useAtom(showingMaskingMenuAtom)
	const peopleMaskingEnabled = isRetouchingEnabled()
	const editor = useEditor()
	const previewOwnerId = useId()
	const setPreviewState = useSetAtom(maskOverlayPreviewStateAtom)
	const setPreviewMasks = useCallback(
		(masks: Mask[]) => {
			setPreviewState(
				masks.length > 0
					? { type: "set", ownerId: previewOwnerId, masks }
					: { type: "clear", ownerId: previewOwnerId },
			)
		},
		[previewOwnerId, setPreviewState],
	)
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [peopleSegmentation, setPeopleSegmentation] =
		useState<VisiblePeopleSegmentationResult | null>()
	const [isPeopleSegmentationLoading, setIsPeopleSegmentationLoading] = useState(false)
	const [peopleSegmentationFailed, setPeopleSegmentationFailed] = useState(false)
	const [isPeopleSubmenuOpen, setIsPeopleSubmenuOpen] = useState(false)
	const [isPeopleSubmenuHovered, setIsPeopleSubmenuHovered] = useState(false)
	const [selectedPeopleIndexes, setSelectedPeopleIndexes] = useState<number[]>([])
	const [createIndividualPeopleMasks, setCreateIndividualPeopleMasks] = useState(false)
	const [selectedPeopleMaskParts, setSelectedPeopleMaskParts] = useState<PeopleMaskPart[]>([
		"entire",
	])
	const [peopleMaskPartAvailability, setPeopleMaskPartAvailability] =
		useState<PeopleMaskPartAvailability | null>(null)
	const currentPhotoId = useAtomValue(currentPhotoIdAtom)
	const transformKey = useEditorPhysicalTransformKey(currentPhotoId)

	const maskProps = useMemo<Partial<BaseMask>>(
		() => ({
			blendMode: props.mode === "add" ? "add" : "subtract",
		}),
		[props.mode],
	)

	useEffect(() => {
		setPeopleSegmentation(undefined)
		setPeopleSegmentationFailed(false)
		setIsPeopleSegmentationLoading(false)
		setIsPeopleSubmenuOpen(false)
		setIsPeopleSubmenuHovered(false)
		setSelectedPeopleIndexes([])
		setPeopleMaskPartAvailability(null)
		setPreviewMasks([])
	}, [setPreviewMasks, transformKey])

	useEffect(() => {
		if (!peopleSegmentation) {
			setSelectedPeopleIndexes([])
			return
		}

		setSelectedPeopleIndexes(peopleSegmentation.persons.map((person) => person.personIndex))
	}, [peopleSegmentation])

	useEffect(() => {
		if (!peopleSegmentation || selectedPeopleIndexes.length === 0) {
			setPeopleMaskPartAvailability(null)
			return
		}

		let canceled = false
		setPeopleMaskPartAvailability(null)
		void MainAIMaskResolver.people
			.getPeopleMaskPartAvailability(editor.photo, selectedPeopleIndexes)
			.then((availability) => {
				if (!canceled) {
					setPeopleMaskPartAvailability(availability)
				}
			})
			.catch(() => {
				if (!canceled) {
					setPeopleMaskPartAvailability(null)
				}
			})

		return () => {
			canceled = true
		}
	}, [editor.photo, peopleSegmentation, selectedPeopleIndexes])

	useEffect(() => {
		if (!peopleMaskPartAvailability) {
			return
		}

		setSelectedPeopleMaskParts((current) =>
			current.filter((part) => part === "entire" || peopleMaskPartAvailability[part]),
		)
	}, [peopleMaskPartAvailability])

	const selectedPeoplePreviewMasks = useMemo(() => {
		if (
			!peopleSegmentation ||
			!isPeopleSubmenuOpen ||
			(!props.isMainMenu && !isPeopleSubmenuHovered) ||
			selectedPeopleIndexes.length === 0 ||
			selectedPeopleMaskParts.length === 0
		) {
			return []
		}

		return createPeopleMasksForSelection({
			selectedPersonIndexes: selectedPeopleIndexes,
			peopleParts: selectedPeopleMaskParts,
			numberedNames: true,
			properties: maskProps,
		})
	}, [
		isPeopleSubmenuOpen,
		isPeopleSubmenuHovered,
		maskProps,
		peopleSegmentation,
		props.isMainMenu,
		selectedPeopleIndexes,
		selectedPeopleMaskParts,
	])

	const peoplePreviewMasks = useMemo(() => {
		if (selectedPeoplePreviewMasks.length === 0) {
			return []
		}

		if (props.isMainMenu) {
			return selectedPeoplePreviewMasks
		}

		return [...(props.parentMasks ?? []), ...selectedPeoplePreviewMasks]
	}, [props.isMainMenu, props.parentMasks, selectedPeoplePreviewMasks])

	useEffect(() => {
		if (!isPeopleSubmenuOpen || peoplePreviewMasks.length === 0) {
			setPreviewMasks([])
			return
		}

		setPreviewMasks(peoplePreviewMasks)

		return () => {
			setPreviewMasks([])
		}
	}, [isPeopleSubmenuOpen, peoplePreviewMasks, setPreviewMasks])

	const loadPeopleSegmentation = useCallback(async () => {
		if (isPeopleSegmentationLoading || peopleSegmentation !== undefined) {
			return
		}

		setIsPeopleSegmentationLoading(true)
		setPeopleSegmentationFailed(false)
		const photo = editor.photo

		try {
			const result = await MainAIMaskResolver.people.getVisiblePeopleSegmentation(photo)
			if (editor.photo.id !== photo.id) {
				return
			}
			setPeopleSegmentation(result)
		} catch {
			if (editor.photo.id !== photo.id) {
				return
			}
			setPeopleSegmentationFailed(true)
			setPeopleSegmentation(null)
		} finally {
			if (editor.photo.id === photo.id) {
				setIsPeopleSegmentationLoading(false)
			}
		}
	}, [editor.photo, isPeopleSegmentationLoading, peopleSegmentation])

	const selectRadialMask = () => {
		props.onSelect([createRadialMask(maskProps)])
	}

	const selectGradientMask = () => {
		props.onSelect([createGradientMask(maskProps)])
	}

	const selectSubjectMask = () => {
		props.onSelect([createSubjectMask(maskProps)])
	}

	const selectBackgroundMask = () => {
		props.onSelect([createBackgroundMask(maskProps)])
	}

	const createSelectedPeopleMasks = () => {
		if (
			!peopleSegmentation ||
			selectedPeopleIndexes.length === 0 ||
			selectedPeoplePreviewMasks.length === 0
		) {
			return
		}

		props.onSelect(selectedPeoplePreviewMasks, {
			separateGroups: props.isMainMenu && createIndividualPeopleMasks,
		})
		setPreviewMasks([])
		setIsPeopleSubmenuOpen(false)
		setIsPeopleSubmenuHovered(false)
		if (props.isMainMenu) {
			setIsExpanded(false)
		} else if (props.parentMaskGroupId) {
			setIsMenuOpen(false)
			setMaskOverlay(props.parentMaskGroupId)
		}
	}

	const selectSkyMask = () => {
		props.onSelect([createSkyMask(maskProps)])
	}

	const selectBrushMask = () => {
		props.onSelect([createBrushMask(maskProps)])
	}

	const allPeopleSelected =
		peopleSegmentation !== null &&
		peopleSegmentation !== undefined &&
		peopleSegmentation.persons.length > 0 &&
		selectedPeopleIndexes.length === peopleSegmentation.persons.length
	const selectedPeopleMaskCount = selectedPeopleIndexes.length * selectedPeopleMaskParts.length
	const createIndividualPeopleMasksLabel =
		selectedPeopleMaskCount > 0
			? t("floatingMasksPanel.createIndividualMasksCount", {
					count: selectedPeopleMaskCount,
				})
			: t("floatingMasksPanel.createIndividualMasks")

	const togglePerson = (personIndex: number) => {
		setSelectedPeopleIndexes((current) => {
			if (current.includes(personIndex)) {
				return current.filter((index) => index !== personIndex)
			}
			return [...current, personIndex].sort((left, right) => left - right)
		})
	}

	const togglePeopleMaskPart = (part: PeopleMaskPart, checked: boolean) => {
		if (part !== "entire" && peopleMaskPartAvailability?.[part] === false) {
			return
		}

		setSelectedPeopleMaskParts((current) => {
			if (part === "entire") {
				return checked ? ["entire"] : []
			}

			const componentParts = current.filter((item) => item !== "entire")
			if (checked) {
				return [...new Set([...componentParts, part])]
			}

			return componentParts.filter((item) => item !== part)
		})
	}

	return (
		<DropdownMenu
			open={props.isMainMenu ? isExpanded : isMenuOpen}
			onOpenChange={(open) => {
				if (props.isMainMenu) {
					setIsExpanded(open)
				} else {
					setIsMenuOpen(open)
				}
				if (!open) {
					setIsPeopleSubmenuOpen(false)
					setIsPeopleSubmenuHovered(false)
					setPreviewMasks([])
					if (!props.isMainMenu && props.parentMaskGroupId) {
						setMaskOverlay(props.parentMaskGroupId)
					} else {
						setMaskOverlay(null)
					}
				}
			}}
		>
			<DropdownMenuTrigger asChild>{props.children}</DropdownMenuTrigger>
			<DropdownMenuContent>
				<DropdownMenuItem
					trackingLabel="create_subject_mask"
					aria-label={t("floatingMasksPanel.ariaCreateSubjectMask")}
					onClick={selectSubjectMask}
				>
					<Icon name="subject_mask" className="mr-2" />
					{t("floatingMasksPanel.subject")}
					{props.isMainMenu && <DropdownMenuShortcut>S</DropdownMenuShortcut>}
				</DropdownMenuItem>
				<DropdownMenuItem
					trackingLabel="create_background_mask"
					aria-label={t("floatingMasksPanel.ariaCreateBackgroundMask")}
					onClick={selectBackgroundMask}
				>
					<Icon name="background_mask" className="mr-2" />
					{t("floatingMasksPanel.background")}
					{props.isMainMenu && <DropdownMenuShortcut>A</DropdownMenuShortcut>}
				</DropdownMenuItem>
				<DropdownMenuItem
					trackingLabel="create_sky_mask"
					aria-label={t("floatingMasksPanel.ariaCreateSkyMask")}
					onClick={selectSkyMask}
				>
					<Icon name="sky_mask" className="mr-2" />
					{t("floatingMasksPanel.sky")}
					{props.isMainMenu && <DropdownMenuShortcut>Y</DropdownMenuShortcut>}
				</DropdownMenuItem>
				<DropdownMenuSub
					open={isPeopleSubmenuOpen}
					onOpenChange={(open) => {
						setIsPeopleSubmenuOpen(open)
						if (open) {
							void loadPeopleSegmentation()
						} else {
							setIsPeopleSubmenuHovered(false)
							setPreviewMasks([])
							if (!props.isMainMenu && props.parentMaskGroupId) {
								setMaskOverlay(props.parentMaskGroupId)
							}
						}
					}}
				>
					<DropdownMenuSubTrigger
						className={clsx(!peopleMaskingEnabled && "hidden")}
						disabled={!peopleMaskingEnabled}
						onPointerMove={() => {
							void loadPeopleSegmentation()
						}}
					>
						<Icon name="people_mask" className="mr-2" />
						{t("floatingMasksPanel.people")}
					</DropdownMenuSubTrigger>
					<DropdownMenuSubContent
						alignOffset={-56}
						className="w-80 border border-base-700 bg-base-800 p-3 shadow-xl"
						onPointerEnter={() => {
							setIsPeopleSubmenuHovered(true)
							if (!props.isMainMenu) {
								setMaskOverlay(null)
							}
						}}
						onPointerLeave={() => {
							setIsPeopleSubmenuHovered(false)
							setPreviewMasks([])
							if (!props.isMainMenu && props.parentMaskGroupId) {
								setMaskOverlay(props.parentMaskGroupId)
							}
						}}
					>
						{isPeopleSegmentationLoading ? (
							<PeopleDetectionLoadingPanel />
						) : (
							<>
								<div>
									{peopleSegmentationFailed &&
										t("floatingMasksPanel.couldNotDetectPeople")}
									{peopleSegmentation === null &&
										!peopleSegmentationFailed &&
										t("floatingMasksPanel.noPeopleDetected")}
									{peopleSegmentation &&
										peopleSegmentation.persons.length > 0 && (
											<div
												className="scrollarea max-h-[188px] overflow-y-auto overflow-x-hidden overscroll-contain pl-1.5 pr-3 pt-1.5"
												onPointerDown={(e) => {
													e.stopPropagation()
												}}
												onWheel={(e) => {
													e.stopPropagation()
												}}
											>
												<div className="grid grid-cols-4 justify-items-center gap-x-2 gap-y-3">
													<PeopleSelectionTile
														label={t("floatingMasksPanel.allPeople")}
														selected={allPeopleSelected}
														thumbnailUrl={undefined}
														trackingValue="all"
														onClick={() => {
															if (allPeopleSelected) {
																setSelectedPeopleIndexes([])
															} else {
																setSelectedPeopleIndexes(
																	peopleSegmentation.persons.map(
																		(person) =>
																			person.personIndex,
																	),
																)
															}
														}}
													/>
													{peopleSegmentation.persons.map((person) => {
														const label = `Person ${person.personIndex + 1}`
														return (
															<PeopleSelectionTile
																key={person.personIndex}
																label={label}
																selected={selectedPeopleIndexes.includes(
																	person.personIndex,
																)}
																thumbnailUrl={person.thumbnailUrl}
																trackingValue={person.personIndex.toString()}
																onClick={() =>
																	togglePerson(person.personIndex)
																}
															/>
														)
													})}
												</div>
												<ScrollBar visible="hover" />
											</div>
										)}
								</div>
								<label className="mt-3 flex items-center gap-3 text-sm">
									<Checkbox
										trackingLabel="people_mask_entire_person"
										checked={selectedPeopleMaskParts.includes("entire")}
										onCheckedChange={(checked) => {
											togglePeopleMaskPart("entire", checked === true)
										}}
									/>
									{t("floatingMasksPanel.entirePerson")}
								</label>
								<DropdownMenuSeparator className="my-3" />
								<div className="space-y-3">
									{selectablePeopleMaskParts.map((part) => (
										<label
											key={part}
											className={clsx(
												"flex items-center gap-3 text-sm",
												peopleMaskPartAvailability?.[part] === false &&
													"text-base-500",
											)}
										>
											<Checkbox
												trackingLabel={`people_mask_${part}`}
												checked={selectedPeopleMaskParts.includes(part)}
												disabled={
													peopleMaskPartAvailability?.[part] === false
												}
												onCheckedChange={(checked) => {
													togglePeopleMaskPart(part, checked === true)
												}}
											/>
											{t(peopleMaskPartLabelKeys[part])}
										</label>
									))}
									{disabledPeopleMaskParts.map((part) => {
										const trackingValue = part
											.split(".")
											.at(-1)
											?.toLowerCase()
											.replaceAll(" ", "_")
										return (
											<label
												key={part}
												className="flex items-center gap-3 text-sm text-base-500"
											>
												<Checkbox
													trackingLabel={`people_mask_${trackingValue}`}
													checked={false}
													disabled
												/>
												{t(part)}
											</label>
										)
									})}
								</div>
								{props.isMainMenu && (
									<>
										<DropdownMenuSeparator className="my-3" />
										<label className="flex items-center gap-3 text-sm">
											<Checkbox
												trackingLabel="people_mask_create_individual_masks"
												checked={createIndividualPeopleMasks}
												onCheckedChange={(checked) =>
													setCreateIndividualPeopleMasks(checked === true)
												}
											/>
											{createIndividualPeopleMasksLabel}
										</label>
									</>
								)}
								<Button
									trackingLabel="create_selected_people_masks"
									aria-label={t("floatingMasksPanel.createSelectedPeopleMasks")}
									className="mt-3 w-full justify-center"
									disabled={
										!peopleSegmentation ||
										selectedPeopleIndexes.length === 0 ||
										selectedPeopleMaskParts.length === 0 ||
										peopleSegmentationFailed
									}
									onClick={createSelectedPeopleMasks}
								>
									{t("floatingMasksPanel.create")}
								</Button>
							</>
						)}
					</DropdownMenuSubContent>
				</DropdownMenuSub>
				<DropdownMenuSeparator />
				<DropdownMenuItem
					trackingLabel="create_radial_mask"
					aria-label={t("floatingMasksPanel.ariaCreateRadialMask")}
					onClick={selectRadialMask}
				>
					<Icon name="radial_mask" className="mr-2" />
					{t("floatingMasksPanel.radialGradient")}
					{props.isMainMenu && (
						<DropdownMenuShortcut className="pl-4">K</DropdownMenuShortcut>
					)}
				</DropdownMenuItem>
				<DropdownMenuItem
					trackingLabel="create_gradient_mask"
					aria-label={t("floatingMasksPanel.ariaCreateGradientMask")}
					onClick={selectGradientMask}
				>
					<Icon name="gradient_mask" className="mr-2" />
					{t("floatingMasksPanel.linearGradient")}
					{props.isMainMenu && (
						<DropdownMenuShortcut className="pl-4">O</DropdownMenuShortcut>
					)}
				</DropdownMenuItem>
				<DropdownMenuItem
					trackingLabel="create_brush_mask"
					aria-label={t("floatingMasksPanel.ariaCreateBrushMask")}
					onClick={selectBrushMask}
				>
					<Icon name="brush_mask" className="mr-2" />
					{t("floatingMasksPanel.brush")}
					{props.isMainMenu && <DropdownMenuShortcut>B</DropdownMenuShortcut>}
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

const PeopleSelectionTile = (props: {
	label: string
	selected: boolean
	thumbnailUrl?: string
	trackingValue: string
	onClick: () => void
}) => {
	return (
		<Button
			variant="custom"
			size="custom"
			trackingLabel="select_people_mask_person"
			trackingValue={props.trackingValue}
			className="group flex w-16 min-w-0 flex-col items-center gap-1 text-xs text-base-300 outline-none"
			onClick={props.onClick}
		>
			<span className="relative w-full">
				<span
					className={clsx(
						"flex aspect-square w-full items-center justify-center overflow-hidden rounded-full border bg-base-900 opacity-100 transition",
						props.selected ? "border-accent-400" : "border-base-600",
					)}
				>
					{props.thumbnailUrl ? (
						<img
							src={props.thumbnailUrl}
							alt=""
							draggable={false}
							className="size-full object-cover hover:opacity-50"
						/>
					) : (
						<Icon name="people_mask" className="size-8 text-base-300" />
					)}
				</span>
				<span
					className={clsx(
						"absolute -left-1 -top-1 flex size-4 items-center justify-center rounded-sm border border-base-400 bg-base-100 text-base-900",
						!props.selected && "opacity-0 group-hover:opacity-60",
					)}
				>
					{props.selected && <Icon name="check" size={12} />}
				</span>
			</span>
			<span className="w-full truncate text-center">{props.label}</span>
		</Button>
	)
}
